// Spec 27 PR 2a — client widget registry.
//
// Mirror of apps/api/src/lib/gen-ui/widget-registry.ts on the
// client. Each widget kind registers a React component; the
// WidgetSurface looks up by kind and renders. Schemas live on the
// server (validation happens before specs reach the client); the
// client trusts the kind discriminant.

import type { ComponentType } from "react";

/**
 * The shape of a widget spec as it arrives from the server. The
 * server has already validated the props against the kind's
 * schema, so on the client we accept it as-is and forward to the
 * registered component.
 */
export interface ClientWidgetSpec {
	id: string;
	kind: string;
	props: unknown;
}

/**
 * Widget state machine — every widget moves through these states.
 * See spec 27 §0 ("State-machined widgets") and §9.6 for the
 * visual treatments of each transition.
 *
 * `idle` and `dirty` are client-driven (the widget itself decides
 * when a user edit qualifies as dirty). `submitting`, `succeeded`,
 * `failed`, and `cancelled` are externally-driven — they correspond
 * to tool_call / tool_result events arriving from the server, and
 * are passed in as `externalState` on WidgetSurface.
 */
export type WidgetState =
	| { kind: "idle" }
	| { kind: "dirty"; values?: unknown }
	| { kind: "submitting"; values?: unknown }
	| { kind: "succeeded"; result?: unknown }
	| { kind: "failed"; error: string }
	| { kind: "cancelled" };

/**
 * Props passed to every widget component. The framework owns
 * `state` and `onAction`; the widget owns rendering and the
 * interactions that lead to actions.
 */
export interface WidgetRenderProps<P = unknown> {
	widgetId: string;
	props: P;
	state: WidgetState;
	/** Emit an action back up to the surface. The surface decides
	 *  whether to update local state, post to the server, or both. */
	onAction: (action: string, values?: unknown) => void;
}

export interface ClientWidgetDef<P = unknown> {
	kind: string;
	label: string;
	component: ComponentType<WidgetRenderProps<P>>;
}

const registry = new Map<string, ClientWidgetDef>();

export function registerClientWidget<P>(def: ClientWidgetDef<P>): void {
	if (registry.has(def.kind)) {
		throw new Error(
			`[gen-ui] client widget kind "${def.kind}" is already registered`,
		);
	}
	registry.set(def.kind, def as ClientWidgetDef);
}

export function getClientWidget(kind: string): ClientWidgetDef | undefined {
	return registry.get(kind);
}

export function listClientWidgets(): ClientWidgetDef[] {
	return Array.from(registry.values());
}
